import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { api, setCsrfToken, setUnauthenticatedHandler, type Me } from './api';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Clients } from './pages/Clients';
import { Connector } from './pages/Connector';

type Session = { state: 'loading' } | { state: 'out' } | { state: 'in'; me: Me };

function Shell() {
  const [session, setSession] = useState<Session>({ state: 'loading' });
  const navigate = useNavigate();

  // Bootstrap: the cookie is the source of truth, so a reload asks the API who
  // we are rather than trusting anything the browser kept.
  useEffect(() => {
    api.me()
      .then((me) => setSession({ state: 'in', me }))
      .catch(() => setSession({ state: 'out' }));
  }, []);

  // Any 401 from anywhere returns the user to the login screen, once.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setCsrfToken(null);
      setSession({ state: 'out' });
      navigate('/login', { replace: true });
    });
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setSession({ state: 'out' });
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  if (session.state === 'loading') {
    return <Layout me={null}><p className="muted">Loading…</p></Layout>;
  }

  if (session.state === 'out') {
    return (
      <Layout me={null}>
        <Routes>
          <Route
            path="/login"
            element={<Login onSignedIn={(me) => { setSession({ state: 'in', me }); navigate('/clients', { replace: true }); }} />}
          />
          {/* Preserve the intended destination through the redirect so a deep
              link survives an expired session. */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Layout me={session.me} onLogout={logout}>
      <Routes>
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:clientId" element={<Connector />} />
        <Route path="*" element={<Navigate to="/clients" replace />} />
      </Routes>
    </Layout>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
